import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ROOMS,
  getRoomBySlug,
  SERVICES_INCLUDED,
  SERVICES_EXCLUDED,
  CHECKIN_POLICY,
} from "../config/rooms";

const PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E";
const onImgError = (e) => {
  e.currentTarget.src = PLACEHOLDER;
};

// ── Icon helpers ─────────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
);
const CrossIcon = () => (
  <svg className="w-5 h-5 text-rose-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const ClockIcon = () => (
  <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function RoomDetail() {
  const { roomSlug } = useParams();
  const room = getRoomBySlug(roomSlug);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [mainImg, setMainImg] = useState(0);

  const images = room ? room.images : [];
  const hasImages = images.length > 0;

  // Keyboard navigation for lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft" && lightboxIndex !== null)
        setLightboxIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight" && lightboxIndex !== null)
        setLightboxIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, images.length]);

  if (!room) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Room not found</h1>
        <Link to="/rooms" className="text-amber-600 hover:underline">
          ← Back to Rooms +
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      {/* ── Hero Banner ─────────────────────────────────────────────────── */}
      <div className="relative w-full h-[38vh] min-h-[200px] bg-gradient-to-br from-amber-700 to-amber-950 overflow-hidden">
        {hasImages && (
          <img
            src={images[mainImg]}
            alt={room.name}
            className="w-full h-full object-cover opacity-60"
            onError={onImgError}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end px-6 pb-8 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="text-white/70 text-sm mb-2 flex items-center gap-1">
            <Link to="/" className="hover:text-amber-300 transition-colors">Home</Link>
            <span>/</span>
            <Link to="/rooms" className="hover:text-amber-300 transition-colors">Rooms +</Link>
            <span>/</span>
            <span className="text-amber-300">{room.name}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white font-fancy drop-shadow-lg">
            {room.name}
          </h1>
        </div>
      </div>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── LEFT: Gallery + Description ───────────────────────────── */}
          <div className="lg:col-span-2 space-y-8">

            {/* Gallery */}
            {hasImages ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-md overflow-hidden"
              >
                {/* Main image */}
                <div
                  className="relative w-full h-[300px] md:h-[420px] cursor-zoom-in overflow-hidden"
                  onClick={() => setLightboxIndex(mainImg)}
                >
                  <img
                    src={images[mainImg]}
                    alt={`${room.name} main`}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    onError={onImgError}
                  />
                  <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    {mainImg + 1} / {images.length} — Click to zoom
                  </div>
                </div>

                {/* Thumbnail strip */}
                {images.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto scroll-smooth" style={{ scrollbarWidth: "none" }}>
                    {images.map((src, idx) => (
                      <button
                        key={idx}
                        onClick={() => setMainImg(idx)}
                        className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          idx === mainImg
                            ? "border-amber-500 shadow-md scale-105"
                            : "border-transparent hover:border-amber-300"
                        }`}
                      >
                        <img
                          src={src}
                          alt={`thumb ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={onImgError}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-dashed border-amber-200 flex flex-col items-center justify-center py-16">
                <svg className="w-16 h-16 text-amber-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-amber-700 font-medium">Images coming soon</p>
              </div>
            )}

            {/* Description */}
            <motion.div
              className="bg-white rounded-2xl shadow-md p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-3 font-fancy">About this Room</h2>
              <p className="text-gray-600 leading-relaxed">{room.description}</p>
            </motion.div>

            {/* Services Included / Excluded */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              {/* Included */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckIcon />
                  </span>
                  <h3 className="text-base font-bold text-gray-800">Services Included</h3>
                </div>
                <ul className="space-y-2.5">
                  {SERVICES_INCLUDED.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <CheckIcon />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Excluded */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center">
                    <CrossIcon />
                  </span>
                  <h3 className="text-base font-bold text-gray-800">Services Excluded</h3>
                </div>
                <ul className="space-y-2.5">
                  {SERVICES_EXCLUDED.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <CrossIcon />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Check-in policy */}
            <motion.div
              className="bg-amber-50 border border-amber-200 rounded-2xl p-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon />
                <h3 className="text-base font-bold text-amber-800">Check-in / Check-out Policy</h3>
              </div>
              <ul className="space-y-2">
                {CHECKIN_POLICY.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Other rooms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <h3 className="text-lg font-bold text-gray-800 mb-4 font-fancy">Explore Other Rooms</h3>
              <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
                {ROOMS.filter((r) => r.slug !== room.slug).map((r) => (
                  <Link
                    key={r.slug}
                    to={`/rooms/${r.slug}`}
                    className="flex-shrink-0 w-48 rounded-xl overflow-hidden shadow-md border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 bg-white"
                  >
                    <div className="w-full h-28 bg-amber-100 overflow-hidden">
                      {r.images.length > 0 ? (
                        <img
                          src={r.images[0]}
                          alt={r.name}
                          className="w-full h-full object-cover"
                          onError={onImgError}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-10 h-10 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-gray-800">{r.name}</p>
                      <p className="text-xs text-amber-600 font-medium mt-0.5">BDT {r.rate}++</p>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>

          {/* ── RIGHT: Pricing & Booking card ─────────────────────────── */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-5">

              {/* Pricing Card */}
              <motion.div
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {/* Card header */}
                <div className="bg-gradient-to-r from-amber-500 to-amber-700 px-6 py-4">
                  <h2 className="text-white font-fancy text-xl font-bold">{room.name}</h2>
                  <p className="text-amber-100 text-sm mt-0.5">Chuti Resort Purbachal</p>
                </div>

                {/* Rate */}
                <div className="px-6 py-5 border-b border-gray-100">
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-extrabold text-gray-900">৳ {room.rate}</span>
                    <span className="text-gray-500 text-sm mb-1">/night ++</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-400 text-sm">৳ {room.previousRate}</span>
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                      Special Rate
                    </span>
                  </div>
                </div>

                {/* Quick info */}
                <div className="px-6 py-4 space-y-3">
                  <InfoRow label="Rooms Available" value={room.noOfRooms} />
                  <InfoRow label="Occupancy / Room" value={room.pax} />
                  <InfoRow label="Check-in" value="02:00 PM" />
                  <InfoRow label="Check-out" value="12:00 Noon" />
                </div>

                {/* CTA Buttons */}
                <div className="px-6 pb-6 space-y-3">
                  <a
                    href="tel:+8801709919827"
                    className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white font-bold py-3 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                    </svg>
                    Book Now — Call Us
                  </a>
                  <a
                    href="https://wa.me/8801709919827"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                    WhatsApp Us
                  </a>
                  <a
                    href="mailto:booking@chutipurbachal.com"
                    className="flex items-center justify-center gap-2 w-full border-2 border-amber-500 text-amber-700 hover:bg-amber-50 font-bold py-3 rounded-xl transition-all duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Booking
                  </a>
                </div>
              </motion.div>

              {/* Note card */}
              <motion.div
                className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <p className="font-semibold mb-2 text-amber-800">★ Note</p>
                <p className="leading-relaxed">
                  Rates are per room per night and subject to applicable taxes (++). Contact us for group bookings, corporate rates or special packages.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Lightbox ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {lightboxIndex !== null && hasImages && (
          <motion.div
            className="fixed inset-0 z-[200] flex flex-col bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* ── Top bar ── */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 md:px-6 py-3 bg-black/40 backdrop-blur-sm border-b border-white/10">
              <span className="text-white font-fancy text-base md:text-lg font-semibold truncate">
                {room.name}
              </span>
              <span className="text-white/60 text-sm font-medium mx-4 whitespace-nowrap">
                {lightboxIndex + 1} / {images.length}
              </span>
              <button
                onClick={() => setLightboxIndex(null)}
                className="flex-shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-red-500 text-white flex items-center justify-center transition-colors duration-200"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* ── Image area with side nav ── */}
            <div className="flex-1 min-h-0 relative flex items-center justify-center px-14 md:px-20 py-4">
              {/* Prev */}
              {images.length > 1 && (
                <button
                  onClick={() => setLightboxIndex((i) => (i - 1 + images.length) % images.length)}
                  className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg z-10"
                  aria-label="Previous"
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              {/* Image */}
              <AnimatePresence mode="wait">
                <motion.img
                  key={lightboxIndex}
                  src={images[lightboxIndex]}
                  alt={`${room.name} ${lightboxIndex + 1}`}
                  className="max-h-full max-w-full object-contain rounded-xl shadow-2xl"
                  style={{ maxHeight: "calc(100vh - 130px)" }}
                  onError={onImgError}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                />
              </AnimatePresence>

              {/* Next */}
              {images.length > 1 && (
                <button
                  onClick={() => setLightboxIndex((i) => (i + 1) % images.length)}
                  className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg z-10"
                  aria-label="Next"
                >
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* ── Thumbnail strip ── */}
            {images.length > 1 && (
              <div className="flex-shrink-0 flex gap-2 overflow-x-auto px-4 py-3 bg-black/40 border-t border-white/10" style={{ scrollbarWidth: "none" }}>
                {images.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setLightboxIndex(idx)}
                    className={`flex-shrink-0 w-14 h-10 md:w-16 md:h-11 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      idx === lightboxIndex
                        ? "border-amber-400 opacity-100 scale-105"
                        : "border-transparent opacity-50 hover:opacity-80 hover:border-white/40"
                    }`}
                  >
                    <img src={src} alt={`thumb ${idx + 1}`} className="w-full h-full object-cover" onError={onImgError} />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Helper: info row in pricing card
function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  );
}

export default RoomDetail;
